"use client";

import {
  forwardRef,
  useCallback,
  type InputHTMLAttributes,
  type CSSProperties,
} from "react";

/** Display size of the clear control (asset is 300×300). */
const CIRCLE = 15;
/** Inset from the field’s inner right edge (px). */
const RIGHT_INSET = 7;
/** Input padding-right so text never sits under the circle. */
const INPUT_PAD_RIGHT = RIGHT_INSET + CIRCLE + 2;

const CLEAR_ICON = "/icons/gray-circle-X.png?v=1";

/**
 * Gray circle / black × — pre-rendered PNG so centering cannot drift.
 * Sits inside the field, right edge, vertically centered via top:50% + translateY(-50%).
 * Asset from trader-dev (`public/icons/gray-circle-X.png`).
 */
export function FieldClearButton({
  onClear,
  hasValue,
}: {
  onClear: () => void;
  hasValue: boolean;
}) {
  return (
    <button
      type="button"
      title="Clear"
      aria-label="Clear"
      tabIndex={-1}
      // Don't steal focus: a combo would treat that blur as "commit what's typed".
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!hasValue) return;
        onClear();
      }}
      style={{
        position: "absolute",
        right: RIGHT_INSET,
        top: "50%",
        transform: "translateY(-50%)",
        width: CIRCLE,
        height: CIRCLE,
        margin: 0,
        padding: 0,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        zIndex: 2,
        lineHeight: 0,
        display: "block",
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={CLEAR_ICON}
        alt=""
        width={CIRCLE}
        height={CIRCLE}
        draggable={false}
        style={{
          display: "block",
          width: CIRCLE,
          height: CIRCLE,
          pointerEvents: "none",
        }}
      />
    </button>
  );
}

const SKIP_TYPES = new Set([
  "checkbox",
  "radio",
  "file",
  "hidden",
  "date",
  "time",
  "datetime-local",
  "month",
  "week",
  "number",
  "range",
  "color",
  "submit",
  "button",
  "reset",
  "image",
]);

/**
 * Classes that belong on the outer box (not the <input>), so absolute
 * positioning is measured against the field border, not margins like mb-2.
 */
const WRAPPER_CLASS_RE =
  /\b(?:w-[\w./\[\]%-]+|min-w-[\w./\[\]%-]+|max-w-[\w./\[\]%-]+|flex-1|flex-auto|flex-none|grow|grow-0|shrink-0|shrink|m-[\w./\[\]%-]+|mx-[\w./\[\]%-]+|my-[\w./\[\]%-]+|mt-[\w./\[\]%-]+|mb-[\w./\[\]%-]+|ml-[\w./\[\]%-]+|mr-[\w./\[\]%-]+)\b/g;

export type ClearableInputProps = InputHTMLAttributes<HTMLInputElement> & {
  wrapperClassName?: string;
  onClear?: () => void;
  /** Override whether the clear control is active. Defaults to non-empty value. */
  hasValue?: boolean;
};

/** Numeric entry fields (prices, qty, OTP, etc.) never get a clear control —
 *  the circle-X is only for free-text fields. Detected via inputMode. */
const NUMERIC_INPUT_MODES = new Set(["numeric", "decimal"]);

/**
 * Text field with clear control fixed inside the border (right, vertically centered).
 * Skips the circle-X for numeric entries (`inputMode="numeric"|"decimal"`) and
 * non-text types (checkbox, date, number, …).
 */
export const ClearableInput = forwardRef<HTMLInputElement, ClearableInputProps>(
  function ClearableInput(
    {
      className = "",
      wrapperClassName = "",
      value,
      defaultValue,
      onChange,
      onClear,
      hasValue: hasValueProp,
      style,
      type = "text",
      disabled,
      readOnly,
      inputMode,
      ...rest
    },
    ref
  ) {
    const isNumericEntry =
      (type && SKIP_TYPES.has(type)) ||
      (inputMode != null && NUMERIC_INPUT_MODES.has(inputMode));

    if (isNumericEntry) {
      return (
        <input
          ref={ref}
          type={type}
          className={className}
          style={style}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          inputMode={inputMode}
          {...rest}
        />
      );
    }

    const strVal =
      value !== undefined && value !== null
        ? String(value)
        : defaultValue !== undefined && defaultValue !== null
          ? String(defaultValue)
          : "";
    const hasValue = hasValueProp ?? strVal.length > 0;

    const handleClear = useCallback(() => {
      if (disabled || readOnly) return;
      if (onChange) {
        const fakeEvent = {
          target: { value: "" },
          currentTarget: { value: "" },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(fakeEvent);
      } else if (ref && typeof ref === "object" && ref.current) {
        ref.current.value = "";
        ref.current.dispatchEvent(new Event("input", { bubbles: true }));
      }
      onClear?.();
    }, [disabled, readOnly, onChange, onClear, ref]);

    const wrapperFromClass = className.match(WRAPPER_CLASS_RE)?.join(" ") ?? "";
    const inputClasses = className
      .replace(WRAPPER_CLASS_RE, "")
      .replace(/\s+/g, " ")
      .trim();

    const {
      width,
      minWidth,
      maxWidth,
      flex,
      flexGrow,
      flexShrink,
      flexBasis,
      margin,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
      ...inputOnlyStyle
    } = (style ?? {}) as CSSProperties;

    const isFull =
      /\bw-full\b/.test(wrapperFromClass) ||
      /\bflex-1\b/.test(wrapperFromClass) ||
      width === "100%";

    const wrapperStyle: CSSProperties = {
      position: "relative",
      display: isFull ? "block" : "inline-block",
      verticalAlign: "middle",
      lineHeight: 0,
      width,
      minWidth,
      maxWidth,
      flex,
      flexGrow,
      flexShrink,
      flexBasis,
      margin,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    };

    const inputStyle: CSSProperties = {
      ...inputOnlyStyle,
      display: "block",
      width: "100%",
      boxSizing: "border-box",
      paddingRight: INPUT_PAD_RIGHT,
      margin: 0,
    };

    return (
      <div
        className={`relative ${wrapperFromClass} ${wrapperClassName}`.trim()}
        style={wrapperStyle}
      >
        <input
          ref={ref}
          type={type}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          disabled={disabled}
          readOnly={readOnly}
          inputMode={inputMode}
          className={inputClasses}
          style={inputStyle}
          {...rest}
        />
        <FieldClearButton hasValue={hasValue} onClear={handleClear} />
      </div>
    );
  }
);
